import { snakeCase } from 'lodash';
import { memo, useCallback, useMemo, useState } from 'react';
import TradeInput from 'src/components/Inputs/TradeInput';
import PriceInputMidButton from 'src/components/TradeForm/PriceInput/PriceInputMidButton';
import { MarketType } from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import useGetTickSizeForMarket, {
	getTickSizeDecimalPlaces,
} from 'src/hooks/useGetTickSizeForMarket';
import { getEffectivePriceDecimals } from 'src/constants/marketDecimalConfig';

interface Props {
	label: string;
	value: string;
	marketIndex: number;
	marketType: MarketType;
	handleChange: (value: string) => void;
	handleBlur?: (value: string) => void;
	showMidButton?: boolean;
	disabled?: boolean;
	className?: string;
	placeholder?: string;
	allowNegative?: boolean;
	baseAssetSymbol: string;
	dataTestId?: string;
}

const PriceInput = memo(function PriceInputMemo({
	label,
	value,
	marketIndex,
	marketType,
	handleChange,
	handleBlur,
	showMidButton,
	disabled,
	className,
	placeholder,
	allowNegative,
	baseAssetSymbol,
	dataTestId,
}: Props) {
	const marketId = new MarketId(marketIndex, marketType);
	const getTickSizeForMarket = useGetTickSizeForMarket();
	const tickSize = getTickSizeForMarket(marketId);
	const tickSizeDecimalPlaces = getTickSizeDecimalPlaces(tickSize);

	// Use the effective decimal places that considers both market config and tick size.
	const effectiveDecimalPlaces = getEffectivePriceDecimals(
		baseAssetSymbol,
		tickSizeDecimalPlaces
	);

	const numberFormatProps = useMemo(() => {
		return {
			maximumFractionDigits: effectiveDecimalPlaces,
		};
	}, [effectiveDecimalPlaces]);

	const [showErrorMessage, setShowErrorMessage] = useState(false);

	const priceError = useMemo(() => {
		if (!value || (!allowNegative && parseFloat(value) === 0)) {
			return 'Please input a valid price';
		}
		return null;
	}, [value, allowNegative]);

	const onBlur = useCallback(() => {
		setShowErrorMessage(true);
		handleBlur?.(value);
	}, [handleBlur, value]);

	const onFocus = useCallback(() => {
		setShowErrorMessage(false);
	}, []);

	return (
		<TradeInput
			id={`${snakeCase(label)}-input`}
			value={value}
			onChange={handleChange}
			label={`${label} (USD)`}
			onBlur={onBlur}
			onFocus={onFocus}
			suffix={
				showMidButton ? (
					<PriceInputMidButton
						marketIndex={marketIndex}
						handleChange={handleChange}
						marketType={marketType}
					/>
				) : undefined
			}
			disabled={disabled}
			inputClassName={className}
			placeholder={placeholder}
			allowNegative={allowNegative}
			numberFormatProps={numberFormatProps}
			error={showErrorMessage && priceError}
			dataTestId={dataTestId}
		/>
	);
});

export default PriceInput;
